/**
 * Pilot statuses (PRD §5.4).
 *
 * Separate from `PilotStatusBadge.tsx` so that file exports only a component —
 * a module mixing components and constants defeats fast refresh.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4
 */

/** The five states a pilot moves through. */
export const PILOT_STATUSES = [
  'SCOPING',
  'CONTRACTING',
  'LIVE',
  'CONCLUDED',
  'ABORTED',
] as const;

export type PilotStatus = (typeof PILOT_STATUSES)[number];

/**
 * Dot colour per status.
 *
 * Deliberately NOT the semantic palette. A concluded pilot is not "success" and
 * an aborted one is not "error" — PRD §5.4 records an outcome separately, and a
 * pilot can conclude having failed or be aborted for good commercial reasons.
 * Tinting the status by sentiment would assert a judgement the data does not
 * make.
 *
 * The three open states walk the same green→navy ramp the funnel uses, so
 * progression reads the same way it does on the startup board. The two closed
 * states step outside it, because they are not positions on that path.
 */
export const PILOT_STATUS_DOT: Record<PilotStatus, string> = {
  SCOPING: 'bg-stage-1',
  CONTRACTING: 'bg-stage-3',
  LIVE: 'bg-stage-5',
  CONCLUDED: 'bg-stage-7',
  ABORTED: 'bg-stage-rejected',
};

/**
 * The same status ramp as CSS colour values, for an inline `style`.
 *
 * `PILOT_STATUS_DOT` gives Tailwind classes, which a dot needs; a Gantt bar
 * sizes its width dynamically and so needs the raw colour beside that width in
 * one `style`. The two must agree, so both are derived from the one ramp.
 */
export const PILOT_STATUS_COLOR: Record<PilotStatus, string> = {
  SCOPING: 'var(--stage-1)',
  CONTRACTING: 'var(--stage-3)',
  LIVE: 'var(--stage-5)',
  CONCLUDED: 'var(--stage-7)',
  ABORTED: 'var(--stage-rejected)',
};

/* No open/closed helper here on purpose: whether a pilot is overdue depends on
   its status AND its planned end, and the server already decides that and sends
   `isOverdue`. A second implementation on the client would be one more place for
   the two to disagree. */

/** Milestone statuses (PRD §5.4). */
export const MILESTONE_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'DONE',
  'SLIPPED',
] as const;

export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

/**
 * Badge tone per milestone status.
 *
 * Here the semantic palette IS right, unlike pilot status: a slipped milestone
 * is unambiguously a problem and a done one is unambiguously good, which is
 * exactly what those tones mean.
 */
export const MILESTONE_TONE: Record<MilestoneStatus, 'ok' | 'info' | 'mute' | 'warn'> = {
  NOT_STARTED: 'mute',
  IN_PROGRESS: 'info',
  DONE: 'ok',
  SLIPPED: 'warn',
};
